import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { Shield, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CaseStudyPreview } from "@/components/case-study-preview";
import { PhotoHero, PhotoProcess, WorkGallery, Checklist } from "@/components/sections/service-photo";

export default function Installation() {
  const installationTypes = [
    { title: "Home AC", desc: "Whisper-quiet systems for bedrooms and living spaces.", img: `${import.meta.env.BASE_URL}images/install-home.webp` },
    { title: "Office AC", desc: "Energy-efficient climate control for productive workspaces.", img: `${import.meta.env.BASE_URL}images/install-office.webp` },
    { title: "Shop AC", desc: "Comfortable environments to keep customers browsing longer.", img: `${import.meta.env.BASE_URL}images/install-shop.webp` },
    { title: "Server Room AC", desc: "Precision 24/7 cooling for critical IT infrastructure.", img: `${import.meta.env.BASE_URL}images/install-server.webp` },
    { title: "Healthcare AC", desc: "Clean, filtered air systems for clinics and surgeries.", img: `${import.meta.env.BASE_URL}images/install-healthcare.webp` },
    { title: "Restaurant AC", desc: "Powerful systems to handle heat loads and busy dining areas.", img: `${import.meta.env.BASE_URL}images/install-restaurant.webp` }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <PhotoHero src="home-installation.webp" alt="VertexAir engineer on a ladder working at wall-mounted Fujitsu outdoor units" position="70% 35%" overlay="bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-slate-900/25" eyebrow="Survey · Design · Install · Commission" testId="hero-installation">
          <h1 className="text-4xl md:text-5xl font-bold font-display mb-4">Air Conditioning Installations London</h1>
          <p className="text-lg md:text-xl text-white/90 mb-6">
            Expert system design and precise installation. Contact us today to arrange a free, no-obligation site survey for your home or business.
          </p>
          <Button size="lg" variant="secondary" className="rounded-full h-14 px-8 text-lg font-bold" data-testid="button-hero-survey" onClick={() => document.getElementById('get-in-touch')?.scrollIntoView({ behavior: 'smooth' })}>
            Request a Free Survey
          </Button>
        </PhotoHero>

        {/* How an installation runs */}
        <PhotoProcess
          testId="section-install-process"
          title="How an Installation Runs"
          intro="Good air conditioning is decided before a single bracket goes up. Each stage below is carried out by our own engineers on site."
          photo="project-5.webp" alt="Ceiling cassette being installed into an open ceiling during first-fix works" accent="bg-primary"
          caption="Ceiling cassette going in during first fix, before the ceiling is closed and finished."
          steps={[
            { title: "Site survey", desc: "We measure the rooms, note glazing, orientation, occupancy and equipment, and look at where indoor and outdoor units can realistically sit." },
            { title: "Sizing & system selection", desc: "Heat load is calculated per room so the unit is matched to the space, then we recommend wall, cassette, ducted or multi-split equipment to suit." },
            { title: "Pipe, cable & condensate routes", desc: "Refrigerant, power and drain routes are planned for the shortest practical runs, correct falls and neat, discreet finishing." },
            { title: "Installation", desc: "Units are fixed level and secure, pipework is brazed or flared, insulated and trunked, and outdoor units are mounted with clearance for airflow and service access." },
            { title: "Pressure test & commissioning", desc: "The system is pressure tested and evacuated before refrigerant is released, then run up and checked in cooling and heating with controls set up and explained." },
          ]}
        />

        <CaseStudyPreview slugs={["kings-gym-chessington-air-conditioning", "kings-gym-croydon-air-conditioning"]} title="Air conditioning installations for Kings Gym" />

        {/* Installation Types Grid */}
        <section className="py-12 lg:py-16 bg-slate-50">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold font-display mb-4">Tailored Solutions for Every Space</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">We supply and install industry-leading air conditioning systems perfectly matched to your specific requirements.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {installationTypes.map((type, idx) => (
                <div key={idx} className="bg-card rounded-2xl overflow-hidden shadow-sm border border-border group">
                  <div className="aspect-video relative overflow-hidden">
                    <img src={type.img} alt={type.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold font-display mb-2">{type.title}</h3>
                    <p className="text-muted-foreground">{type.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Workmanship Gallery */}
        <WorkGallery
          testId="gallery-installation"
          title="Installation Workmanship"
          intro="Photographs from our own installation work: outdoor plant, rooftop positions and ceiling units."
          items={[
            { src: "project-15.webp", alt: "Daikin outdoor units neatly positioned", caption: "Daikin outdoor units placed with clearance for airflow and service access." },
            { src: "project-21.webp", alt: "Rooftop outdoor unit installation", caption: "Rooftop outdoor unit installation in progress." },
            { src: "project-7.webp", alt: "Ceiling cassette in an unfinished ceiling", caption: "Ceiling cassette fitted at first-fix stage, ceiling still to be finished." },
            { src: "home-commercial-installation.webp", alt: "Commercial ceiling air conditioning works", caption: "Commercial ceiling works with services routed above the grid." },
          ]}
        />

        {/* What to expect */}
        <Checklist
          testId="section-install-checklist"
          iconClass="text-primary"
          title="What We Check Before Handover"
          intro="Commissioning is where an installation proves itself. These are the points we work through before leaving site."
          items={[
            { title: "Pressure & vacuum", desc: "Pipework is pressure tested for leaks and evacuated to remove air and moisture before refrigerant is introduced." },
            { title: "Condensate drainage", desc: "Drains are tested with water to confirm they run freely, including any condensate pump." },
            { title: "Electrical connections", desc: "Supplies, isolators and interconnecting cables are checked and terminations secured." },
            { title: "Operating temperatures", desc: "The system is run in cooling and heating and air temperatures are checked across the indoor unit." },
            { title: "Controls set-up", desc: "Remote, wall or app controls are configured and we show you how to use timers and modes." },
            { title: "Tidy finish", desc: "Trunking, covers and penetrations are made good and the work area is left clean." },
          ]}
        />

        {/* About Us and Services */}
        <section className="py-12 lg:py-16 bg-background border-y border-border">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-display mb-8">About Us and Our Services</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>Vertex Climate was founded on a simple principle: to raise the standard of air conditioning installations in London. We bypass the shortcuts and focus purely on enduring quality and meticulous engineering.</p>
              <p>Every system we install is carefully calculated based on heat load, room dynamics, and aesthetic preferences. Our expert engineers are fully employed, extensively trained, and deeply respectful of your property.</p>
            </div>
          </div>
        </section>

        {/* Our Warranty */}
        <section className="py-12 lg:py-14 bg-primary/5">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="bg-card rounded-3xl p-8 md:p-12 shadow-xl border border-border flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                <Shield className="w-12 h-12 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-display mb-4">Our Comprehensive Warranty</h2>
                <p className="text-lg text-muted-foreground mb-4">Because we are approved installers for the industry's leading brands, we can offer extended warranties on all new installations—giving you total peace of mind for years to come.</p>
                <ul className="grid sm:grid-cols-2 gap-3">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary" /> Up to 7 years warranty on parts</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-primary" /> 1-year workmanship guarantee</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Approved Installer / Brands */}
        <section className="py-12 lg:py-14 bg-background border-t border-border text-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl font-bold font-display mb-10 text-muted-foreground">Approved Installers For</h2>
            <div className="flex flex-wrap justify-center gap-12 opacity-60 grayscale">
              {/* Simulated Logos using text for safety since we don't have the assets, but they look pro */}
              <div className="text-2xl font-bold tracking-tighter">DAIKIN</div>
              <div className="text-2xl font-bold tracking-tighter">MITSUBISHI ELECTRIC</div>
              <div className="text-2xl font-bold tracking-tighter">FUJITSU</div>
              <div className="text-2xl font-bold tracking-tighter">PANASONIC</div>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
